<template>
    <div class="increase-chart-content">
        <div class="increase-chart-info">
            <div class="increase-chart-info-item">
                <div class="increase-chart-info-item-header">
                    <PtIcon icon="SolidTimeRefresh"/>
                    <div class="increase-chart-info-item-header-title">{{ $t('dataCoverage.updateFrequency') }}</div>
                </div>
                <div class="increase-chart-info-item-content">
                    <div class="increase-chart-info-item-content-text">
                        {{increaseChartData.updateFrequency}}
                    </div>
                </div>
            </div>

            <div class="increase-chart-info-item">
                <div class="increase-chart-info-item-header">
                    <PtIcon icon="SolidTimer"/>
                    <div class="increase-chart-info-item-header-title">{{ $t('dataCoverage.lastUpdateDate') }}</div>
                </div>
                <div class="increase-chart-info-item-content">
                    <div class="increase-chart-info-item-content-text">{{increaseChartData.updateDate}}</div>
                </div>
            </div>

            <div class="increase-chart-info-item">
                <div class="increase-chart-info-item-header">
                    <PtIcon icon="SolidRefineFilterNew"/>
                    <div class="increase-chart-info-item-header-title">{{ $t('dataCoverage.timeRange') }}</div>
                </div>
                <div class="increase-chart-info-item-content">
                    <div class="custom-select-wrapper">
                        <DropdownSelect
                            :config="options"
                            :value="currentTimeRange"
                            width="164px"
                            @item-click="handleChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div v-if="isChartLoading" class="increase-chart-chart-loading">
            <PtIcon icon="SolidLoadingBars"/>
        </div>
        <div v-show="!isChartLoading" id="increase-chart-chart" class="increase-chart-chart"></div>
    </div>
</template>

<script>
import { numberFormatUtil } from '../../../util/index';
import { max, min, fill } from 'lodash';
import { Icon as PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/Timer.svg';
import '@patsnap-ui/icon/assets/solid/TimeRefresh.svg';
import '@patsnap-ui/icon/assets/solid/RefineFilterNew.svg';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import DropdownSelect from '../../../components/dropdown-select/DropdownSelect';

export default {
    name: 'IncreaseChartContent',
    props: {
        isChartLoading: {
            type: Boolean,
            isRequired: true,
        },
        timeRange: {
            type: [String, Number],
            isRequired: true,
        },
        increaseChartData: {
            type: Object,
            isRequired: true,
        },
    },
    components: {
        DropdownSelect,
        PtIcon,
    },
    data() {
        return {
            currentTimeRange: '',
            options: [
                { label: this.$t('dataCoverage.timeRangeOptions1'), value: 1 },
                { label: this.$t('dataCoverage.timeRangeOptions2'), value: 3 },
                { label: this.$t('dataCoverage.timeRangeOptions3'), value: 5 }
            ],
        };
    },
    watch: {
        'increaseChartData': {
            handler() {
                this.init();
            },
            immediate: true
        }
    },
    methods: {
        init() {
            this.$nextTick(()=>{
                const myChart = this.$echarts.init(document.getElementById('increase-chart-chart'));
                // 18274047 => 15050005 除第一位，剩余的如果大于5 取5 否则为0
                let minTotalQuantity = min(this.increaseChartData.totalQuantityList.map(item => item.value));
                const minTotalQuantityArr = minTotalQuantity.toString().split('');
                for (let i = 1; i < minTotalQuantityArr.length; i++) {
                    minTotalQuantityArr[i] = minTotalQuantityArr[i] > 5 ? 5 : 0;
                }
                minTotalQuantity = minTotalQuantityArr.join('');
                const options = {
                    grid: {
                        top: 20,
                        left: 30,
                        bottom: 0,
                        right: 30,
                        containLabel: true
                    },
                    tooltip: {
                        trigger: 'axis',
                        confine: true,
                        backgroundColor: '#FEFEFE',
                        enterable: true,
                        extraCssText: 'font-weight:normal;max-width: 400px;max-height:280px;box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.3); text-align: left; overflow-y: auto; overflow-x: auto;white-space: normal;z-index: 99;border-color: rgb(254,254,254) !important;',
                        textStyle: {
                            color: '#020A1A',
                            fontSize: 12
                        }
                    },
                    legend: {
                        show: false,
                    },
                    xAxis: {
                        type: 'category',
                        axisTick: {
                            show: false
                        },
                        axisLabel: {
                            rotate: -45,
                            color: '#061632',
                        },
                        axisLine: {
                            show: true,
                            lineStyle: {
                                color: '#DFE1E6'
                            }
                        },
                        data: this.increaseChartData.totalQuantityList.map(item => item.name)
                    },
                    yAxis: [
                        {
                            type: 'value',
                            min: 0,
                            max: max(this.increaseChartData.incrementList.map(item => item.value)),
                            splitLine: {
                                show: false
                            },
                            axisLabel: {
                                formatter: (value) => {
                                    return numberFormatUtil(value);
                                }
                            }
                        },
                        {
                            type: 'value',
                            min: minTotalQuantity,
                            max: max(this.increaseChartData.totalQuantityList.map(item => item.value)),
                            splitLine: {
                                lineStyle: {
                                    color: '#DFE1E6',
                                    type: 'dashed'
                                },
                                show: true
                            },
                            axisLabel: {
                                formatter: (value) => {
                                    return numberFormatUtil(value);
                                }
                            }
                        },
                    ],
                    series: [
                        {
                            name: this.$t('dataCoverage.totalQuantity'),
                            type: 'line',
                            color: '#33B900',
                            lineStyle: {
                                color: '#33B900'
                            },
                            areaStyle: {
                                color: 'rgba(51,185,0,0.05)'
                            },
                            yAxisIndex: 1,
                            smooth: true,
                            symbol: 'none',
                            data: this.increaseChartData.totalQuantityList,
                        },
                        {
                            barMinHeight: 10,
                            name: this.$t('dataCoverage.increment'),
                            yAxisIndex: 0,
                            type: 'bar',
                            color: '#B9D5F1',
                            itemStyle: {
                                color: '#B9D5F1',
                                borderRadius: 4,
                            },
                            emphasis: {
                                itemStyle: {
                                    color: '#B9D5F1',
                                },
                            },
                            data: this.increaseChartData.incrementList
                        },

                    ]
                };
                myChart.setOption(options);
            });
        },
        handleChange(value) {
            this.currentTimeRange = value;
            this.$emit('changeYear', value);
        },
    },
    created() {
        this.currentTimeRange = this.timeRange;
    }
};
</script>

<style lang="scss">
.increase-chart-content {
    position: relative;

    .increase-chart-info {
        display: flex;
        justify-content: space-between;

        .increase-chart-info-item {
            &-header {
                display: flex;
                align-items: center;

                .increase-chart-info-item-header-title {
                    height: 22px;
                    font-size: 16px;
                    font-weight: 500;
                    color: $text-hint;
                    line-height: 22px;
                }
            }

            &-content {
                margin-top: 2px;

                .increase-chart-info-item-content-text {
                    height: 32px;
                    font-size: 24px;
                    font-weight: bold;
                    color: #33B900;
                    line-height: 32px;
                }

                .custom-select-popper {
                    margin-top: 4px !important;

                    .el-select-dropdown__list {
                        padding: 8px;

                        .el-select-dropdown__item {
                            padding: 0 8px !important;
                            color: $text-primary;

                            &.selected {
                                color: $status-blue-primary;
                            }

                            &.hover {
                                background: rgba(179, 186, 197, 0.2);
                                border-radius: 4px;
                            }
                        }
                    }
                }

                .custom-select-wrapper {
                    position: relative;
                    width: 164px;
                }
            }
        }
    }

    .increase-chart-chart, .increase-chart-chart-loading {
        margin-top: 16px;
        width: 670px;
        height: 388px;
    }

    .increase-chart-chart-loading {
        display: flex;
        justify-content: center;
        align-items: center;
    }
}
</style>
